import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { createApiUrl } from '../utils/apiConfig';
import './ResetPassword.css';

const ResetPassword = () => {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrorMessage('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match. Please try again.');
      return false;
    }
    if (formData.password.length < 8 || formData.confirmPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long. Please try again.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token || String(token).trim() === '') {
      setErrorMessage('Invalid reset link. Request a new password reset email.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const response = await axios.post(createApiUrl(`/api/reset-password/${encodeURIComponent(token)}`), {
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      if (response.data.success) {
        setPasswordChanged(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'An error occurred. Please try again.');
    }
  };

  return (
    <div className="resetPasswordContainer">
      <h1 className="resetPasswordHeading">Reset<br />Password</h1>
      <div className="resetPasswordCard">
        {!isOnline && (
          <div className="alert alert-warning">You are currently offline</div>
        )}

        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}

        {!token && (
          <div className="alert alert-danger">
            This password reset link is invalid or incomplete. Please use &quot;Forgot password&quot; to request a new email.
          </div>
        )}

        {passwordChanged && (
          <div className="alert alert-success">
            Your password was successfully changed. Please login with your new password.
          </div>
        )}

        <form onSubmit={handleSubmit} className="resetPasswordForm">
          <div className="formField">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="formInput"
              required
              placeholder="New Password"
              minLength="8"
            />
            <label htmlFor="password" className="formLabel">New Password:</label>
            <button
              type="button"
              className="togglePasswordBtn"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <FaEye /> : <FaEyeSlash />}
            </button>
          </div>

          <div className="formField">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="formInput"
              required
              placeholder="Confirm New Password"
              minLength="8"
            />
            <label htmlFor="confirmPassword" className="formLabel">Confirm New Password:</label>
            <button
              type="button"
              className="togglePasswordBtn"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
            </button>
          </div>

          <button type="submit" className="submitButton" disabled={!token}>
            Reset Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword; 